//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'

//#endregion

import { Activity } from '../../../models/common/activity'
import { Enums } from '../../../models/common/enums'
import { IReferenceType, IAlarmConfigurable } from '../../../models/util/interfaces'
import { OpData, OpDevice, OpDeviceHierarchy, OpMeter } from '../../../models/core'
import { OpDataTypeInGroup } from '../../../models/core/op-data-type-in-group'
import { OpSchema } from '../../../models/custom'
import { SchemaModel } from '../../api/api.models'

import { EventsNames } from '../../../helpers/events-names'

import { ActionHelper } from '../../../helpers/action-helper'
import { CommonHelper } from '../../../helpers/common-helper'
import { ConfigHelper } from '../../../helpers/config-helper'
import { ConvertHelper } from '../../../helpers/convert-helper'
import { IUserSession } from '../../../helpers/user-session'
import { LocalizationHelper } from '../../../helpers/localization-helper'
import { LogHelper } from '../../../helpers/log-helper'
import { OperatorLayoutSettings } from '../../../helpers/operator-layout-settings'
import { ReferenceTypeComponent } from '../../../helpers'
import { TemplateHelper } from '../../../helpers/template-helper'
import { ToastHelper } from '../../../helpers/toast-helper'

import { DataTypeGroupComponent } from '../../../helpers/data-type-group-component'

import { DeviceService } from '../../../services/device'
import { SchemaService } from '../../../services/schema'

import { ActionStatusOptions } from '../../common/action-status/action-status.controller'
import { ConnectedObject } from '../../common/connected-object/connected-object.directive'
import { Details, DetailsAccordion, DetailsAccordionSection, DetailsScope } from '../../common/common.details'
import { DetailsOptions, DetailsPopupOptions } from '../../common/details-popup/details-popup.directive'
import { DetailsParametersOptions } from '../../common/details-parameters/details-parameters.directive'
import { GridBridge } from '../../common/common.interfaces'
import { GridController, GridOptions, GridTypes } from '../../common/grid/grid.controller'
import { HistorySource } from '../../common/history/history.directive.controller'
import { InfoPopupSource } from '../../common/info-popup/info-popup.directive'
import { LocalDictionaryFactory } from '../../api/api.module'
import { LoggerService } from '../../common/common.module'
import { MeasurementsBridge } from '../../common/measurements/measurements.controller'
import { TreeListOptions } from '../../common/tree-list/tree-list.controller'

import { DefinitionsBridge } from '../../meters/meters.controller'
import { SystemService } from '../../../services/system'
import { IAlarmDefinitionsEditOptions } from '../../common/alarm-definition/alarm-definitions-edit'
import { AlarmDefinitionsEditController } from '../../common/alarm-definition/alarm-definitions-edit/alarm-definitions-edit.directive.controller'
import { AlarmDefinitionsOptions, IAlarmDefinitions } from '../../common/alarm-definition/alarm-definitions'

interface DevicesDetailsScope
    extends DetailsScope {
    DEVICE_COMMON_TAB_NAME: string
    DEVICE_MEASUREMENTS_TAB_NAME: string
    DEVICE_HISTORY_TAB_NAME: string

    DEVICE_PARAMS_ACCORDION_NAME: string
    DEVICE_EXTENDED_PARAMS_ACCORDION_NAME: string
    DEVICE_PARENT_DEVICES_ACCORDION_NAME: string
    DEVICE_CHILD_DEVICES_ACCORDION_NAME: string
    ALARM_DEF_DEVICE_ACCORDION_NAME: string

    DELETE_CONFIRMATION_POPUP_ID: string

    ACTION_RUN_POPUP_ID: string
    ACTION_STATUS_POPUP_ID: string
    INFO_POPUP_ID: string

    id: number
    editId: number
    popupFor: any

    extendedParamGridOptions: TreeListOptions
    detailsParametersOptions: DetailsParametersOptions

    detailedParamGridOptions: TreeListOptions
    parentDevicesGridOptions: GridOptions
    childDevicesGridOptions: GridOptions
    childDevicesGridEditOptions: GridBridge

    historySource: HistorySource
    measurementsBridge: MeasurementsBridge
    connectedObject: ConnectedObject
    schemas: SchemaModel[]

    deleteConfirmationOptions: {
        title: string
        onConfirmed: () => void
    }
    needInfoPopup: boolean
    infoPopupSource: InfoPopupSource

    definitionsBridge: DefinitionsBridge
    alarmDefsWithTemplates: any[]

    alarmDefinitionsOptions: AlarmDefinitionsOptions
    alarmDefinitionsEditOptions: {
        alarmDefinitionsOptions: IAlarmDefinitionsEditOptions,
        onInitialized: (component: AlarmDefinitionsEditController) => void
    }
}

export class DevicesDetailsDirectiveController
    extends Details<DevicesDetailsScope>
{
    // Members

    private referenceObject: OpDevice
    private referenceObjectId: number

    private childDevicesGrid: GridController<OpDeviceHierarchy>
    private currentMeter: OpMeter

    private alarmDefinitionsComponent: IAlarmDefinitions
    private alarmDefinitionsEditController: AlarmDefinitionsEditController

    // Members End

    constructor(
        $scope: DevicesDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        private systemService: SystemService,
        protected userSession: IUserSession,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName,
        protected deviceService: DeviceService,
        protected schemaService: SchemaService
    ) {
        super($scope, $timeout, $q, logger, localDictionary, OperatorLayoutSettings.DevicePopupTabActive, userSession, referenceType)
        this.$scope.editId = $scope.$id
        this.setConfirmationsOptions()
        this.setEvents()
        this.setInfoPopup()
        this.setOptions()
        this.setTemplateConsts()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    // Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options
        this.$scope.popupFor = CommonHelper.device

        this.referenceObjectId = options.referenceObjectId

        this.onClear()
        this.onRefresh()
        this.setHistoryOptions()
        this.showPopup()
    }

    private onRefresh(toRefresh: OpDevice = null): void {
        const deferred: JQueryDeferred<OpDevice> = $.Deferred<OpDevice>()

        if (!toRefresh) {
            this.userSession
                .getDataProvider()
                .Device
                .getDevice(this.referenceObjectId)
                .then((result: OpDevice) => {
                    if (result != null && result.currentMeter != null && !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceAlarmDef)) {
                        return this.userSession
                            .getDataProvider().Meter
                            .getMeter(result.currentMeter.idMeter)
                            .then((meter: OpMeter) => {
                                this.currentMeter = meter

                                deferred.resolve(result)
                            })
                    }
                    else {
                        deferred.resolve(result)
                    }
                })
                .fail((error: any) => {
                    const errorMsg: string = ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator)
                    ToastHelper.showError(errorMsg)
                    LogHelper.error(DeviceService.LOG_PREFIX, errorMsg)

                    deferred.resolve(null)
                })
                .always(() => {
                    this.$scope.showTabs = true
                })
        } else {
            deferred.resolve(toRefresh)
        }

        deferred
            .promise()
            .then((result: OpDevice) => {
                if (result && (!this.referenceObject || this.referenceObject.serialNbr == result.serialNbr)) {
                    this.$scope.popupTitle = result.toString()

                    this.referenceObject = result

                    this.setBasicParameters()
                    this.setExtendedParameters()
                    this.setParentDevicesGridOptions()
                    this.setConnectedObjectOptions()
                    this.initializeAlarmDefinitions()
                }
            })
            .then(() => {
                this.setSchemas()
                return this.onRefreshChildDevices()
            })
            .always(() => {
                this.setMeasurementOptions()
            })
    }

    private onRefreshChildDevices(): JQueryGenericPromise<void> {
        return this.userSession
            .getDataProvider()
            .Device
            .getChildDevices(this.referenceObject.serialNbr)
            .then((deivceHierarchy: OpDeviceHierarchy[]) => {
                this.setChildDevicesGridEdit(deivceHierarchy)
                this.setChildDevicesGridOptions(deivceHierarchy)
            })
            .fail((error: any) => {
                this.setChildDevicesGridEdit([])
                this.setChildDevicesGridOptions([])

                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
            })
    }

    private onParamsEdit(selectedTabType: Enums.SimaxCreateEditSelectedTabType): void {
        this.$scope.$root.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, this.deviceService.getCreateEditOptions(this.userSession, this.referenceObjectId, this.referenceObject, selectedTabType))
    }

    private onEditChildDevices(): void {
        this.$scope.$broadcast(EventsNames.OPEN_GRID_EDIT_POPUP + this.$scope.detailsPopupId + 'childDevices')
    }

    private onClear(): void {
        this.$scope.popupTitle = ''
        this.$scope.showTabs = false

        this.referenceObject = null
        this.$scope.detailsParametersOptions = null
        this.$scope.extendedParamGridOptions = null

        this.$scope.detailedParamGridOptions = null
        this.$scope.parentDevicesGridOptions = null
        this.$scope.childDevicesGridOptions = null
        this.$scope.childDevicesGridEditOptions = null
        this.$scope.historySource = null
        this.$scope.measurementsBridge = null
        this.$scope.connectedObject = null
        this.$scope.schemas = null
        this.$scope.needInfoPopup = null
    }

    private onClose(): void {
        this.hidePopup()
    }

    private onConfirmDelete(): void {
        this.deviceService
            .deleteDevice(this.userSession.getDataProvider(), this.referenceObjectId)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
                this.onClose()
            })
            .fail((error: any) => {
                LogHelper.error(DeviceService.LOG_PREFIX, error)
            })
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.DELETE_CONFIRMATION_POPUP_ID,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onParentDeviceDoubleClick(arg: any): void {
        let device: OpDevice = null

        if (arg != null) {
            if (arg instanceof OpDeviceHierarchy) {
                device = (<OpDeviceHierarchy>arg).deviceParent
            } else if (arg.data && arg.data.deviceParent && arg.data.deviceParent instanceof OpDevice) {
                device = <OpDevice>arg.data.deviceParent
            }
        }

        this.openDeviceDetails(device)
    }

    private onChildDeviceDoubleClick(arg: any): void {
        let device: OpDevice = null

        if (arg != null) {
            if (arg instanceof OpDeviceHierarchy) {
                device = (<OpDeviceHierarchy>arg).device
            } else if (arg.data && arg.data.device && arg.data.device instanceof OpDevice) {
                device = <OpDevice>arg.data.device
            }
        }

        this.openDeviceDetails(device)
    }

    private onSaveChildDevices(changed: any[]): ng.IPromise<boolean> {
        const deferred: ng.IDeferred<boolean> = this.$q.defer<boolean>()

        const deviceHierarchy: OpDeviceHierarchy[] = changed.map((x: any) => <OpDeviceHierarchy>x)

        this.userSession
            .getDataProvider()
            .Device
            .saveChildDevices(deviceHierarchy)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ChildDevicesSaved));
                deferred.resolve(true)
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                deferred.reject(false)
            })

        return deferred.promise;
    }

    private onExtendedParameterCellPrepared(options: any): void {
        if (options != null
            && options.rowType == "data"
            && options.column.dataField == "value"
            && options.data) {
            if (options.data instanceof OpData) {
                const data: OpData = options.data

                if (data.dataType == null) {
                    data.dataType = this.userSession
                        .getDataProvider()
                        .DataType
                        .getDataTypeLocal(data.idDataType)
                }

                if (data.value != null) {
                    if (data.dataType.idReferenceType != null) {
                        if (data.dataType.idReferenceType === this.referenceType && this.referenceObject != null && data.value === this.referenceObject.getReferenceKey()) {
                            options.cellElement.html(this.referenceObject.toString())
                        } else {
                            ReferenceTypeComponent.getReferenceObject(this.userSession.getDataProvider(), data.dataType.idReferenceType, data.value)
                                .then((referenceObject: IReferenceType) => {
                                    options.cellElement.html(referenceObject == null ? null : referenceObject.toString())
                                })
                        }
                    } else {
                        options.cellElement.html(ConvertHelper.prepareDataValue(data.value, data.dataType))
                    }
                }
            } else if (options.data.rows) {
                this.$q.all(options.data.rows.map((row: OpData) => {
                    if (row) {
                        if (row.value != null) {
                            if (row.dataType.idReferenceType != null) {
                                if (row.dataType.idReferenceType === this.referenceType && this.referenceObject != null && row.value === this.referenceObject.getReferenceKey()) {
                                    return this.referenceObject.toString()
                                } else {
                                    return ReferenceTypeComponent.getReferenceObject(this.userSession.getDataProvider(), row.dataType.idReferenceType, row.value)
                                        .then((referenceObject: IReferenceType) => {
                                            return referenceObject == null ? null : referenceObject.toString()
                                        })
                                }
                            } else {
                                return ConvertHelper.prepareDataValue(row.value, row.dataType)
                            }
                        }
                    }

                    return null
                })).then((values: any) => {
                    options.cellElement.html(values.join(';'))
                })
            }
        }
    }

    private onChildDevicesClosed(): void {
        if (this.childDevicesGrid != null) {
            this.childDevicesGrid.dxGrid.refresh()
        }
    }

    // Events End


    //#region Alarm Definitions

    private getAlarmDefAccordionStatus() {
        const alarmDefCount: number = this.alarmDefinitionsComponent == null
            ? 0
            : this.alarmDefinitionsComponent.getCurrentAlarmDefs().length

        return `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DefinedCount)}${(alarmDefCount)}`
    }

    private initializeAlarmDefinitions() {
        if (!this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceAlarmDef)) {
            this.initializeAlarmDefOptions()
            this.initializeAlarmDefinitionsEditOptions()
        }
    }

    private initializeAlarmDefinitionsEditOptions(): void {
        this.$scope.alarmDefinitionsEditOptions = {
            alarmDefinitionsOptions: {
                configurationReferenceObject: this.referenceObject,
                moduleName: Enums.SimaxModuleName.Meter,
                referenceType: Enums.ReferenceType.IdMeter,
                referenceObject: this.currentMeter,
                sendConfigurationIdActivity: Activity.DEVICE_CONFIGURATION_SEND,

                sendConfiguration: () => {
                    this.onSendDeviceConfiguration()

                    return $.Deferred<any>().resolve().promise()
                },

                onAlarmConfigurableSaved: (alarmConfigurable: OpMeter) => {
                    this.currentMeter = alarmConfigurable
                    this.$scope.alarmDefinitionsEditOptions.alarmDefinitionsOptions.referenceObject = alarmConfigurable

                    return this.onAlarmConfigurableSaved(alarmConfigurable)
                },
            },
            onInitialized: (component: AlarmDefinitionsEditController) => {
                this.alarmDefinitionsEditController = component
            }
        }
    }

    private initializeAlarmDefOptions(): void {
        this.$scope.alarmDefinitionsOptions = {
            isColumnReorderAllowed: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DeviceAlarmDef),

            referenceType: Enums.ReferenceType.IdMeter,
            referenceObjectId: this.currentMeter != null ? this.currentMeter.idMeter : null,

            onInitialized: (component: IAlarmDefinitions) => {
                this.alarmDefinitionsComponent = component
            }
        }
    }

    private onAlarmConfigurableSaved(alarmConfigurable: IAlarmConfigurable): void {
        if (alarmConfigurable == null) {
            return null
        }

        if (this.alarmDefinitionsComponent != null) {
            this.alarmDefinitionsComponent.setDataSource(alarmConfigurable.currentAlarmDefs)
        }
    }

    private openAlarmDefEdit(): void {
        if (this.currentMeter != null) {
            const hasEditPermission: boolean = this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_EDIT)
            const notReadonlyPermission: boolean = !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceAlarmDef)

            if (hasEditPermission && notReadonlyPermission && this.alarmDefinitionsEditController != null) {
                this.alarmDefinitionsEditController.showSingleEdit()
            } else {
                LogHelper.log(DeviceService.LOG_PREFIX, `onLocationAlarmDefEdit: hasEditPermission: ${hasEditPermission}, notReadonlyPermission: ${notReadonlyPermission}, alarmDefinitionEdit: ${(this.alarmDefinitionsEditController == null ? '<null>' : 'Exists')}`)
            }
        } else {
            ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.YouCantEditAlarmDefinitionsIfDeviceHasNoMeterAssigned))
        }
    }

    //#endregion


    // Setup

    public getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceParameters)
            || !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceGeneralSchema)) {
            const accordionSections: DetailsAccordionSection[] = []

            accordionSections.push({
                name: this.$scope.DEVICE_PARAMS_ACCORDION_NAME,
                showInTab: this.$scope.DEVICE_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,

                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceParameters),

                editMethod: () => this.onParamsEdit(Enums.SimaxCreateEditSelectedTabType.BaseParameters)
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.DeviceDetailsAccordions, 0, accordionSections))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceExtendedParameters)) {
            const accordionSections: DetailsAccordionSection[] = []

            accordionSections.push({
                name: this.$scope.DEVICE_EXTENDED_PARAMS_ACCORDION_NAME,
                showInTab: this.$scope.DEVICE_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SpecificParameters),
                visible: true,

                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceExtendedParameters),

                editMethod: () => this.onParamsEdit(Enums.SimaxCreateEditSelectedTabType.ExtendedParameters)
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.DeviceDetailsAccordions, 1, accordionSections))
        }

        const parentDevicesVisible: boolean = !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceParenntDevices)
        const childDevicesVisible: boolean = !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceChildDevices)
        const alarmDefinitionsVisible: boolean = !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceAlarmDef, true)

        if (parentDevicesVisible || childDevicesVisible || alarmDefinitionsVisible) {
            const accordionSections: DetailsAccordionSection[] = []

            if (parentDevicesVisible) {
                accordionSections.push({
                    name: this.$scope.DEVICE_PARENT_DEVICES_ACCORDION_NAME,
                    showInTab: this.$scope.DEVICE_COMMON_TAB_NAME,
                    showStatus: true,
                    title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ParentDevices),
                    visible: true,

                    canEdit: false,

                    status: () => this.getAmountDescription(this.$scope.parentDevicesGridOptions)
                })
            }

            if (childDevicesVisible) {
                accordionSections.push({
                    name: this.$scope.DEVICE_CHILD_DEVICES_ACCORDION_NAME,
                    showInTab: this.$scope.DEVICE_COMMON_TAB_NAME,
                    showStatus: true,
                    title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ChildDevices),
                    visible: true,

                    canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_EDIT)
                        && !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceChildDevices),

                    editMethod: () => this.onEditChildDevices(),

                    status: () => this.getAmountDescription(this.$scope.childDevicesGridOptions)
                })
            }

            if (alarmDefinitionsVisible) {
                accordionSections.push({
                    name: this.$scope.ALARM_DEF_DEVICE_ACCORDION_NAME,
                    showInTab: this.$scope.DEVICE_COMMON_TAB_NAME,
                    showStatus: true,
                    title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlarmDefs),
                    visible: true,

                    canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_EDIT)
                        && !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceAlarmDef),

                    editMethod: () => {
                        this.openAlarmDefEdit()
                    },

                    status: () => {
                        return this.getAlarmDefAccordionStatus()
                    },
                })
            }

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.DeviceDetailsAccordions, 2, accordionSections))
        }

        return accordions
    }

    public getTabs(): any[] {
        const tabs: any = []

        tabs.push({
            template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common), ConfigHelper.Icon.HOME, this.userSession),
            name: this.$scope.DEVICE_COMMON_TAB_NAME
        })

        if (this.userSession.getLoggedOperator().hasPermission(Activity.MEASUREMENTS_LIST)
            && !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceMeasurements)) {
            tabs.push({
                template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Measurements), ConfigHelper.Icon.MEASUREMENTS, this.userSession),
                name: this.$scope.DEVICE_MEASUREMENTS_TAB_NAME
            });
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceHistory)) {
            tabs.push({
                template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.History), ConfigHelper.Icon.HISTORY, this.userSession),
                name: this.$scope.DEVICE_HISTORY_TAB_NAME
            });
        }

        return tabs;
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private getActions(): DevExpress.ui.dxButtonOptions[] {
        const actions: DevExpress.ui.dxButtonOptions[] = []

        if (this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_CONFIGURATION_CONSISTENCY_CHECK_OP)) {
            actions.push({
                disabled: false,

                template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConfigurationConsistencyCheck), ConfigHelper.Icon.ACTIONS, this.userSession),
                hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConfigurationConsistencyCheck),

                width: '100%',

                onInitialized: (e: any) => { },
                onClick: (e: any) => this.onGetDeviceConfigurationInconsistency(e)
            })
        }

        if (this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_CONFIGURATION_SEND)) {
            actions.push({
                disabled: false,

                template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SendConfigurationToDevice), ConfigHelper.Icon.DEVICES, this.userSession),
                hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SendConfigurationToDevice),

                width: '100%',

                onInitialized: (e: any) => { },
                onClick: (e: any) => this.onSendDeviceConfiguration(),
            })
        }

        return actions
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => this.onOpen(args[3])
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )

        this.$scope.$on(
            EventsNames.CLOSE_CREATE_EDIT_POPUP,
            (event: ng.IAngularEvent, ...args: any[]) => this.onRefresh(args[0])
        )

        this.$scope.$on(
            EventsNames.UPDATE_SCHEMAS,
            (event: ng.IAngularEvent, ...args: any[]) => this.setSchemas()
        )

        this.$scope.$on(
            EventsNames.CLOSE_GRID_EDIT_POPUP + this.$scope.detailsPopupId + 'childDevices',
            (event: ng.IAngularEvent, ...args: any[]) => this.onChildDevicesClosed()
        )
    }

    private setOptions(): void {
        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,

            actions: this.getActions(),

            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),

            delHasPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DEL) ? Activity[Activity.DEVICE_DEL] : null,

            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),

            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }

    private setBasicParameters(): void {
        this.$scope.detailsParametersOptions = null

        DataTypeGroupComponent
            .getDataTypeInGroupByModule(
                this.userSession.getDataProvider(),
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
                this.moduleName,
                Enums.ReferenceType.IdDeviceType,
                this.referenceObject.idDeviceType
            )
            .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                this.$scope.detailsParametersOptions = {
                    dataTypes: dataTypeInGroup,
                    referenceType: this.referenceType,
                    referenceObject: this.referenceObject,
                    useDynamicValues: true,
                }
            })
    }

    private setExtendedParameters(): void {
        this.$scope.detailedParamGridOptions = null

        const showLegend: boolean = true

        this.$scope.detailedParamGridOptions = this.deviceService.getExtendedParametersOptions(true)
        this.$scope.detailedParamGridOptions.onCellPrepared = (options: any) => { this.onExtendedParameterCellPrepared(options) }

        $.when(
            DataTypeGroupComponent.getDataTypeInGroupByModule(this.userSession.getDataProvider(),
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS_TO_DISPLAY,
                this.moduleName,
                Enums.ReferenceType.IdDeviceType,
                this.referenceObject.idDeviceType
            ),
            DataTypeGroupComponent.getDataTypeInGroupByModule(this.userSession.getDataProvider(),
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS_TO_EDIT,
                this.moduleName,
                Enums.ReferenceType.IdDeviceType,
                this.referenceObject.idDeviceType
            )
        )
            .then((toDisplay: OpDataTypeInGroup[], toEdit: OpDataTypeInGroup[]) => {
                if (toDisplay == null) { toDisplay = [] }
                if (toEdit == null) { toEdit = [] }

                const idDataTypes: number[] = toDisplay.concat(toEdit).map((dtig: OpDataTypeInGroup) => dtig.idDataType)

                if (idDataTypes.length > 0) {
                    this.$scope.detailedParamGridOptions.dataSource = this.deviceService.createDataSourceForExtendedParameters(this.userSession.getDataProvider(), this.referenceObject, idDataTypes);
                }
            })
    }

    private setParentDevicesGridOptions(): void {
        this.$scope.parentDevicesGridOptions = null

        this.userSession
            .getDataProvider()
            .Device
            .getParentDevices(this.referenceObject.serialNbr)
            .then((deivceHierarchy: OpDeviceHierarchy[]) => {
                this.$scope.parentDevicesGridOptions = {
                    name: "parentDevices",
                    type: GridTypes.NO_SEARCH_BAR_SMALL,
                    buttonsOptions: {
                        enabled: true
                    },
                    addCustomFilters: true,
                    columnsDictionary: this.deviceService.getParentDevicesGridColumns(),
                    onRowDoubleClick: (e: any) => this.onParentDeviceDoubleClick(e),

                    dataSource: deivceHierarchy,

                    allowColumnReordering: !this.userSession
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DeviceParenntDevices),
                }
            })
    }

    private setChildDevicesGridOptions(deviceHierarchy: OpDeviceHierarchy[]): void {
        this.$scope.childDevicesGridOptions = null

        this.$scope.childDevicesGridOptions = {
            name: "childDevices",
            type: GridTypes.NO_SEARCH_BAR_SMALL,
            buttonsOptions: {
                enabled: true
            },
            addCustomFilters: true,
            columnsDictionary: this.deviceService.getChildDevicesGridColumns(),

            onInitialized: (e: any, grid: GridController<OpDeviceHierarchy>) => this.childDevicesGrid = grid,
            onRowDoubleClick: (e: any) => this.onChildDeviceDoubleClick(e),

            dataSource: deviceHierarchy,

            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DeviceChildDevices),
        }
    }

    private setChildDevicesGridEdit(hierarchy: OpDeviceHierarchy[]): void {
        this.$scope.childDevicesGridEditOptions = null

        this.$scope.childDevicesGridEditOptions = {
            id: this.referenceObjectId,
            referenceId: this.referenceType,

            columns: this.deviceService.getChildDevicesGridColumns(),

            name: 'childDevicesGrid',
            popupId: this.$scope.detailsPopupId + 'childDevices',
            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ChildDevices),

            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DeviceChildDevices),

            items: hierarchy,

            onSubmit: (added: any[], removed: any[], inGrid: any[]) => this.onSaveChildDevices(inGrid)
        }
    }

    private setHistoryOptions(): void {
        this.$scope.historySource = {
            dataMethodName: null,   // TODO: To delete in future
            id: null,               // TODO: To delete in future

            name: 'devicesHistory',
            referenceType: this.referenceType,
            referenceValue: this.referenceObjectId,

            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DeviceHistory),

            onRowDoubleClick: null
        }
    }

    private setConnectedObjectOptions(): void {
        this.$scope.connectedObject = {
            connectedObjectMethod: 'meter',

            connectedObjectTitle: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CurrentMeter),
            connectedObjectVisibility: this.referenceObject.currentMeter != null
                && !this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceParametersCurrentMeter),

            connectedObjectId: this.referenceObject.currentMeter != null
                ? this.referenceObject.currentMeter.idMeter
                : null,

            connectedObjectName: this.referenceObject.currentMeter != null
                ? this.referenceObject.currentMeter.toString()
                : null,

            connectedObjectAllowLink: this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.METER_LIST)
        }
    }

    private setSchemas(): void {
        this.$scope.schemas = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceGeneralSchema)) {
            this.userSession
                .getDataProvider()
                .System
                .getSchema(this.referenceType, [this.referenceObjectId])
                .then((schemas: OpSchema[]) => {
                    if (schemas != null && schemas.length > 0) {
                        schemas = this.schemaService.limitSchemaCount(schemas)

                        const simpleXmlSchemas: SchemaModel[] = schemas
                            .filter((schema: OpSchema) => { return schema.class === Enums.LocationSchemaClass.SimpleXml })
                            .map((schema: OpSchema) => { return CommonHelper.opSchemaToSchemaModel(schema, this.userSession) })

                        if (simpleXmlSchemas.length > 0) {
                            this.$scope.schemas = simpleXmlSchemas
                        }
                    }

                    return schemas
                })
                .fail((error) => {
                    const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                    ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                    LogHelper.warn(DeviceService.LOG_PREFIX, "Unable to load schema.")
                })
        }
    }

    private setMeasurementOptions(): void {
        if (this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DeviceMeasurements)) {
            return
        }

        let devices: { id: number, name: string, referenceId: number }[] = []

        devices.push({
            id: this.referenceObjectId,
            name: this.referenceObject.toString(),
            referenceId: this.referenceObject.idDeviceType
        })

        if (this.$scope.childDevicesGridOptions.dataSource != null) {
            devices = devices.concat(
                this.$scope
                    .childDevicesGridOptions
                    .dataSource
                    .filter((hierarchy: OpDeviceHierarchy) => hierarchy != null && hierarchy.device != null && hierarchy.slotNbr > 0)
                    .map((hierarchy: OpDeviceHierarchy) => {
                        return {
                            id: hierarchy.serialNbr,
                            name: hierarchy.device.toString(),
                            referenceId: this.referenceType
                        }
                    })
            )
        }

        this.$scope.measurementsBridge = {
            id: this.referenceObjectId,
            referenceType: this.referenceType,
            requestProperty: 'deviceIds',

            devices: devices,

            allowColumnReordering: this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DeviceMeasurements),

            hideChartOptions: this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_CHART_OPTION_HIDDEN, Enums.UIObjectProperties.DeviceMeasurements),

            hideDataTypePanel: this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_MEASUREMENT_DATA_TYPE_OPTION_HIDDEN, Enums.UIObjectProperties.DeviceMeasurements),

            chartOptionsPermissions: null,          // TODO: To delete in future
            chartDataTypeOptionsPermissions: null,  // TODO: To delete in future
            latestDataMethodName: null,             // TODO: To delete in future
            dataMethodName: null,                   // TODO: To delete in future
        }
    }

    private setInfoPopup(): void {
        this.$scope.infoPopupSource = {
            infoText: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConfigurationConsistent),
            popupTitle: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConfigurationConsistencyCheck),
        }
    }

    private setTemplateConsts(): void {
        this.$scope.DEVICE_COMMON_TAB_NAME = 'DEVICE_COMMON_TAB_NAME'
        this.$scope.DEVICE_MEASUREMENTS_TAB_NAME = 'DEVICE_MEASUREMENTS_TAB_NAME'
        this.$scope.DEVICE_HISTORY_TAB_NAME = 'DEVICE_HISTORY_TAB_NAME'

        this.$scope.DEVICE_PARAMS_ACCORDION_NAME = 'DEVICE_PARAMS_ACCORDION_NAME'
        this.$scope.DEVICE_EXTENDED_PARAMS_ACCORDION_NAME = 'DEVICE_EXTENDED_PARAMS_ACCORDION_NAME'
        this.$scope.DEVICE_PARENT_DEVICES_ACCORDION_NAME = 'DEVICE_PARENT_DEVICES_ACCORDION_NAME'
        this.$scope.DEVICE_CHILD_DEVICES_ACCORDION_NAME = 'DEVICE_CHILD_DEVICES_ACCORDION_NAME'
        this.$scope.ALARM_DEF_DEVICE_ACCORDION_NAME = 'ALARM_DEF_DEVICE_ACCORDION_NAME'

        this.$scope.DELETE_CONFIRMATION_POPUP_ID = this.$scope.detailsPopupId + '_DELETE_CONFIRMATION_POPUP'

        this.$scope.ACTION_RUN_POPUP_ID = this.$scope.detailsPopupId + '_ACTION_RUN_POPUP'
        this.$scope.ACTION_STATUS_POPUP_ID = this.$scope.detailsPopupId + '_ACTION_STATUS_POPUP'

        this.$scope.INFO_POPUP_ID = this.$scope.detailsPopupId + '_INFO_POPUP'
    }

    // Setup End

    // Device Configuration

    private onSendDeviceConfiguration(): void {
        ActionHelper
            .sendDeviceConfiguration(this.userSession.getDataProvider(), [this.referenceObject])
            .then((actionStatusOptions: ActionStatusOptions) => {
                this.$scope.$broadcast(EventsNames.SHOW_ACTION_STATUS_POPUP + this.$scope.ACTION_STATUS_POPUP_ID, actionStatusOptions)
            })
    }

    private onGetDeviceConfigurationInconsistency(e: any): void {
        const serialNumbers: number[] = [this.referenceObject.serialNbr]

        this.userSession
            .getDataProvider().Device
            .getDeviceConfigurationInconsistency(serialNumbers)
            .then((dataList: OpData[]) => {
                this.deviceService
                    .getDeviceConfigurationInconsistencyOptions(this.userSession.getDataProvider(), dataList, serialNumbers, false)
                    .then((treeOptions: any) => {
                        this.$scope.needInfoPopup = true
                        this.$scope.extendedParamGridOptions = treeOptions

                        this.$timeout(() => {
                            this.$scope.$broadcast(EventsNames.OPEN_INFO_POPUP_POPUP + this.$scope.INFO_POPUP_ID, dataList, treeOptions)
                        }, 500)
                    })
                    .fail((error: any) => {
                        ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DeviceConfigurationInconsistencyError));

                        LogHelper.error(DeviceService.LOG_PREFIX, error)
                    })
            })
    }

    // Device Configuration End

    // Utility


    private getAmountDescription(gridOptions: GridOptions): string {
        return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Amount)
            + ': '
            + (gridOptions != null && gridOptions.dataSource != null && gridOptions.dataSource.length ? gridOptions.dataSource.length : 0)
    }

    private openDeviceDetails(device: OpDevice): void {
        if (device != null) {
            this.systemService.openDetails(this.deviceService.getDetailsOptions(device.serialNbr))
        }
    }

    // Utility End

    // Backward Compatibility - DO NOT USE, DELETE IN FUTURE

    public resetData(): void { this.onClear() }
    public loadData(id: number, objects: any[]): void { this.onOpen(null) }
    public updateData(result: any): void { this.onRefresh(result) }

    // Backward Compatibility End
}